import React, { useEffect, useState } from 'react';
import './settings.scss';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { Button, Icons } from '../../../components/base/BootstrapComponents/BootstrapComponents';
import FieldDTO from '../../../components/fields/Abstract/FieldDTO';
import TextInputWithLabel from '../../../components/fields/TextInput/TextInputWithLabel';
import ComplexDTO from '../../../dto/ComplexDTO';
import { getSettingsData } from '../reducer';
import {
  fallbackCopyTextToClipboard,
  getResponseError,
  responseLogWithNotification,
  useUpdatePeriod,
} from '../../../utils/utils';
import Switches from '../../../components/fields/Switches/Switches';
import CoordinateConverter from './CoordinateConverter';
import {
  loadGeneralSettings,
  saveGeneralSettings,
} from '../actions';
import Loader from '../../../components/base/Loader/Loader';
import { getSystemData } from '../../System/reducer';
import { errorNotification, successNotification } from '../../Notifications/notificationDecorator';

export default function CameraSettingsPage() {
  const dispatch = useDispatch();
  const [isEdit, setIsEdit] = useState(false);
  const {
    generalSettings = {},
    loadingGeneralSettings = false,
    loadingSaveGeneralSettings = false,
  } = useSelector(getSettingsData);
  const { updatePeriod } = useSelector(getSystemData);

  const DTO = new ComplexDTO();
  const attributes = DTO.getAttributesByName();
  const intl = useIntl();
  const { formatMessage } = intl;
  const {
    control,
    reset,
    setValue,
    getValues,
  } = useForm({
    mode: 'onBlur',
    defaultValues: generalSettings,
    resetOptions: {
      keepDirtyValues: true, // user-interacted input will be retained
      keepErrors: true, // input errors will be retained with value update
    },
  });

  const loadData = () => {
    const p1 = dispatch(loadGeneralSettings()).catch(responseLogWithNotification);
    return Promise.all([p1]);
  };

  const [setPeriod] = useUpdatePeriod(loadData, updatePeriod);
  useEffect(() => setPeriod(updatePeriod), [updatePeriod]);
  React.useEffect(() => {
    reset(generalSettings);
  }, [generalSettings]);

  const save = () => {
    const data = new ComplexDTO(getValues()).getData();
    dispatch(saveGeneralSettings(data)).then(() => {
      loadData();
      successNotification(formatMessage({ id: 'settings.save' }), formatMessage({ id: 'settings.save.success' }));
    }).catch(response => {
      errorNotification(formatMessage({ id: 'settings.save' }), getResponseError(response));
    });
  };
  const cancel = () => {
    reset();
    setIsEdit(false);
  };
  const copyGPS = () => {
    fallbackCopyTextToClipboard(`(${generalSettings.longitude || 0}, ${generalSettings.latitude || 0})`);
    successNotification('Копирование координат', 'Успешно');
  };

  const handleChangeCoordinate = ({ longitude = 0, latitude = 0 }) => {
    setValue('longitude', longitude);
    setValue('latitude', latitude);
  };

  const editForm = () => (
    <div className="settings-options frame">
      <div className="settings-edit">
        <div className="settings-header">
          <div className="settings-label">{formatMessage({ id: 'menu.settings_camera' })}</div>
        </div>
        <div className="settings-form">
          <div className="settings-view-form">
            <FieldDTO
              control={control}
              component={TextInputWithLabel}
              dtoObject={attributes.name}
              variant="material"
            />
            <FieldDTO
              control={control}
              component={TextInputWithLabel}
              dtoObject={attributes.serial_number}
              variant="material"
              className="mt-3"
            />
            <FieldDTO
              control={control}
              component={TextInputWithLabel}
              dtoObject={attributes.address}
              variant="material"
              className="mt-3"
            />
            <div className="f4 mt-3 mb-3">{formatMessage({ id: 'complex.gps' })}</div>
            <FieldDTO
              control={control}
              component={Switches}
              dtoObject={attributes.auto_coordinates}
              variant="material"
            />

            <div className="settings-coordinate-row mt-3">
              <FieldDTO
                control={control}
                component={TextInputWithLabel}
                dtoObject={attributes.longitude}
                variant="material"
              />
              <FieldDTO
                control={control}
                component={TextInputWithLabel}
                dtoObject={attributes.latitude}
                variant="material"
              />
              <CoordinateConverter onChange={handleChangeCoordinate} />
            </div>
            <div className="hr-row mt-3" />
            <Button variant="secondary" className="copy-button mt-3" onClick={copyGPS}>
              <Icons width={16} height={16} customIcon icon="copy" className="button-icons" />
              {formatMessage({ id: 'copy.gps' })}
            </Button>
          </div>
        </div>
        <div className="settings-footer">
          <Button className="clear-show" onClick={save}>{formatMessage({ id: 'app.save' })}</Button>
          <Button className="clear-data" variant="secondary" onClick={cancel}>{formatMessage({ id: 'app.cancel' })}</Button>
        </div>
      </div>
    </div>
  );

  const viewForm = () => (
    <div className="settings-options frame">
      <div className="settings-edit">
        <div className="settings-header">
          <div className="settings-label">{formatMessage({ id: 'column.checkbox' })}</div>
        </div>
        <div className="settings-form">
          <div className="settings-view-form">
            <div className="settings-view-row">
              <div className="settings-view-label">{formatMessage({ id: 'complex.name' })}:</div>
              <div className="settings-view-value">{generalSettings.name || ''}</div>
            </div>
            <div className="settings-view-row">
              <div className="settings-view-label">{formatMessage({ id: 'complex.serial.number' })}:</div>
              <div className="settings-view-value">{generalSettings.serial_number || ''}</div>
            </div>
            <div className="settings-view-row">
              <div className="settings-view-label">{formatMessage({ id: 'complex.address' })}:</div>
              <div className="settings-view-value">{generalSettings.address || ''}</div>
            </div>
            <div className="settings-view-row">
              <div className="settings-view-label">{formatMessage({ id: 'complex.gps' })}:</div>
              <div className="settings-view-value" />
            </div>
            <div className="settings-view-value">
              {formatMessage({ id: 'longitude' })} — {generalSettings.longitude || ''} °<br />
              {formatMessage({ id: 'latitude' })} — {generalSettings.latitude || ''} °
            </div>
            <div className="hr-row mt-3" />
            <Button variant="secondary" className="copy-button mt-3" onClick={copyGPS}>
              <Icons width={16} height={16} customIcon icon="copy" className="button-icons" />
              {formatMessage({ id: 'copy.gps' })}
            </Button>
          </div>
        </div>
        <div className="settings-footer">
          <Button
            className="clear-show"
            onClick={() => {
              setIsEdit(true);
            }}
          >{formatMessage({ id: 'app.edit' })}
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Loader loading={loadingGeneralSettings || loadingSaveGeneralSettings} />
      { isEdit ? editForm() : viewForm() }
    </>
  );
}
